import {
  Assessment,
  AutoAwesome,
  BusinessSharp,
  ChatBubbleOutline,
  CreditScore,
  EventNote,
  Feed,
  Insights,
  Leaderboard,
  LocalHospital,
  LocalPharmacy,
  MedicalInformation,
  Payment as PaymentIcon,
  PeopleAlt,
  PriceCheck,
} from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import MedicationIcon from "@mui/icons-material/Medication";
import {
  Box,
  Drawer,
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import type { FC } from "react";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import useEntitlements from "../../hooks/useEntitlements";
import PencilAlt from "../../icons/PencilAlt";
import UsersIcon from "../../icons/Users";
import { useSelector } from "../../store";
import { TNavSection } from "../../types/navItem";
import {
  ADMIN_ROLES,
  ALL_ROLES,
  CLINICAL_REVENUE_ADMIN_ROLES,
  EXECUTIVE_ROLES,
  CLINICAL_ROLES,
  OPERATIONS_ROLES,
  SUBSCRIPTION_MANAGER_ROLES,
  MEDICAL_ROLES,
} from "../../utils/roleUtils";
import { filterNavSections } from "../../utils/shouldRenderMenuItem";
import NavSection from "../NavSection";
import Scrollbar from "../Scrollbar";

interface DashboardSidebarProps {
  onMobileClose: () => void;
  openMobile: boolean;
  collapsed: boolean;
  handleToggleCollapsed: () => void;
}

export const dashboardSidebarSections: TNavSection[] = [
  {
    title: "Dashboard",
    items: [
      {
        title: "Executive Dashboard",
        path: "/home/summary",
        ldFlag: "allowExecutiveDashboard",
        icon: <Leaderboard fontSize="small" />,
        allowedRoles: EXECUTIVE_ROLES,
        capability: "feature_executive_dash",
      },
      {
        title: "Overview",
        path: "/home",
        ldFlag: "homeDashboard",
        icon: <Insights fontSize="small" />,
        capability: "feature_home_dash",
      },
      {
        title: "Appointments",
        path: "/appointments",
        ldFlag: "appointmentsFeature",
        icon: <EventNote fontSize="small" />,
        capability: "feature_appointments",
        allowedRoles: [...ALL_ROLES],
      },
      {
        title: "Messages",
        path: "/communications/broadcast",
        ldFlag: "broadcastFeature",
        icon: <ChatBubbleOutline fontSize="small" />,
        capability: "feature_messaging",
        allowedRoles: [...OPERATIONS_ROLES, ...ADMIN_ROLES],
      },
    ],
  },
  {
    // No section-level clinical-tools capability: AI Scribe–only orgs still need
    // AI Notes here while encounters / care plans / prescriptions stay item-gated.
    title: "Clinical Records",
    ldOrgFlag: "medicalRecordEnabled",
    allowedRoles: [...MEDICAL_ROLES],
    items: [
      // {
      //   title: "Care Insights",
      //   path: "/health/summary",
      //   ldFlag: "healthInformationSummaryFeature",
      //   ldOrgFlag: "medicalRecordEnabled",
      //   icon: <QueryStats fontSize="small" />,
      //   allowedRoles: MEDICAL_ROLES,
      //   capability: "feature_clinical_insights",
      // },
      {
        title: "Medical Encounters",
        path: "/health/encounters",
        ldFlag: "medicalEncountersFeature",
        ldOrgFlag: "medicalRecordEnabled",
        icon: <MedicalInformation fontSize="small" />,
        allowedRoles: [...MEDICAL_ROLES],
        capability: "feature_clinical_encounters",
      },
      {
        title: "AI Notes",
        path: "/ai-notes",
        // Release flag: keep off in prod until AI Notes is ready to ship.
        ldFlag: "allowAiNotes",
        ldOrgFlag: "medicalRecordEnabled",
        icon: <AutoAwesome fontSize="small" />,
        allowedRoles: CLINICAL_ROLES,
        capability: "ai_scribe",
      },
      {
        title: "Care Plans",
        path: "/health/plans",
        ldFlag: "carePlanFeature",
        ldOrgFlag: "medicalRecordEnabled",
        icon: <PencilAlt fontSize="small" />,
        allowedRoles: CLINICAL_ROLES,
        capability: "feature_clinical_care_plans",
      },
      {
        title: "Prescriptions",
        path: "/health/prescriptions",
        ldFlag: "prescriptionFeature",
        ldOrgFlag: "medicalRecordEnabled",
        icon: <LocalPharmacy fontSize="small" />,
        allowedRoles: CLINICAL_ROLES,
        capability: "feature_clinical_prescriptions",
      },
    ],
  },
  {
    title: "Clinical Tools",
    ldOrgFlag: "medicalRecordEnabled",
    capability: "feature_clinical_tools",
    allowedRoles: CLINICAL_ROLES,
    items: [
      {
        title: "Triage Board",
        path: "/health/triage",
        ldFlag: "allowTriageDashboard",
        ldOrgFlag: "medicalRecordEnabled",
        icon: <Assessment fontSize="small" />,
        allowedRoles: CLINICAL_ROLES,
        capability: "feature_clinical_triage",
      },
      {
        title: "Medication Administration",
        path: "/health/medication-administrations",
        ldFlag: "medicationAdministrationFeature",
        ldOrgFlag: "medicalRecordEnabled",
        icon: <MedicationIcon fontSize="small" />,
        allowedRoles: CLINICAL_ROLES,
        capability: "feature_clinical_med_admin",
      },
    ],
  },
  {
    title: "Billing",
    ldFlag: "billingFeature",
    ldOrgFlag: "billingEnabled",
    tier: ["PREMIUM", "BASIC", "TRIAL", "ESSENTIALS", "ENTERPRISE"],
    capability: "billing_access",
    allowedRoles: CLINICAL_REVENUE_ADMIN_ROLES,
    items: [
      // {
      //   title: "Inventory",
      //   path: "/inventory",
      //   icon: <Warehouse fontSize="small" />,
      // },
      {
        title: "Summary",
        path: "/billing",
        icon: <CreditScore fontSize="small" />,
        capability: "billing_access",
        allowedRoles: CLINICAL_REVENUE_ADMIN_ROLES,
      },
      {
        title: "Activities",
        path: "/billing/activities",
        icon: <Feed fontSize="small" />,
        capability: "billing_access",
        allowedRoles: CLINICAL_REVENUE_ADMIN_ROLES,
      },
      {
        title: "Billable Items",
        path: "/billing/billable-items",
        icon: <PriceCheck fontSize="small" />,
        capability: "billing_access",
        allowedRoles: CLINICAL_REVENUE_ADMIN_ROLES,
      },
      {
        title: "Online Payments",
        path: "/billing/payments",
        icon: <PaymentIcon fontSize="small" />,
        capability: "billing_access",
        allowedRoles: CLINICAL_REVENUE_ADMIN_ROLES,
        ldFlag: "enableOnlinePayments",
      },
    ],
  },
  {
    title: "Patient Management",
    items: [
      {
        title: "Patient List",
        path: "/patients",
        icon: <PeopleAlt fontSize="small" />,
        capability: "feature_patient_mgmt",
      },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        title: "Team Members",
        path: "/staff",
        icon: <UsersIcon fontSize="small" />,
        capability: "member_management",
        allowedRoles: ["Subscription Manager", "Administrator"],
      },
      {
        title: "Facility",
        path: "/facility",
        icon: <BusinessSharp fontSize="small" />,
        capability: "feature_facility_mgmt",
      },
      {
        // Org details editing — available to all plans (including AI Scribe–only).
        title: "Practice Info",
        path: "/practice-info",
        icon: <LocalHospital fontSize="small" />,
      },
      {
        title: "Organization",
        path: "/organization",
        icon: <CorporateFareIcon fontSize="small" />,
        capability: "member_management",
        allowedRoles: ["Subscription Manager", "Administrator"],
      },
    ],
  },
];

const DashboardSidebar: FC<DashboardSidebarProps> = ({
  onMobileClose,
  openMobile,
  collapsed,
  handleToggleCollapsed,
}) => {
  const flags = useFlags();
  const location = useLocation();
  const currentOrg = useSelector((state) => state.user.currentOrganization);
  const userRoles = useSelector((state) => state.user.roles);
  const { can, entitlements } = useEntitlements();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const [activeSections, setActiveSections] = useState(
    dashboardSidebarSections,
  );
  const entitlementTier = entitlements?.tier;

  useEffect(() => {
    if (flags && currentOrg && userRoles) {
      setActiveSections(
        filterNavSections(
          dashboardSidebarSections,
          flags,
          currentOrg.id,
          userRoles,
          can,
          currentOrg.authId,
          entitlementTier,
          currentOrg.adminSettings,
        ),
      );
    }
  }, [flags, currentOrg, userRoles, can, entitlementTier]);

  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (openMobile && onMobileClose && lastPath.current !== location.pathname) {
      onMobileClose();
    }
    lastPath.current = location.pathname;
  }, [location.pathname, onMobileClose, openMobile]);

  const content = (
    <nav aria-label="Dashboard Sidebar Navigation">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
        }}
      >
        <Scrollbar options={{ suppressScrollX: true, suppressScrollY: true }}>
          {lgUp && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: collapsed ? "50%" : 16,
                transform: collapsed ? "translateX(50%)" : "none",
                zIndex: 10,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "flex-end",
                mt: 1.5,
                mb: 0.5,
                backgroundColor: "background.paper",
                width: "fit-content",
                cursor: "pointer",
              }}
            >
              <Tooltip
                title={collapsed ? "Expand menu" : "Collapse menu"}
                placement="right"
              >
                <IconButton
                  onClick={handleToggleCollapsed}
                  size="small"
                  sx={{
                    transition: "transform 0.2s",
                    transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box sx={{ p: collapsed ? 0 : 2 }}>
            {activeSections.map((section) => (
              <NavSection
                key={section.title}
                pathname={location.pathname}
                collapsed={collapsed}
                sx={{
                  "& + &": {
                    mt: 1,
                  },
                }}
                {...section}
              />
            ))}
          </Box>
        </Scrollbar>
      </Box>
    </nav>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: "background.paper",
            height: "calc(100% - 56px) !important",
            top: "56px !Important",
            width: collapsed ? 72 : 280,
            transition: "width 0.3s ease",
            overflowX: "hidden",
          },
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={openMobile}
      PaperProps={{
        sx: {
          backgroundColor: "background.paper",
          width: 280,
        },
      }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

export default DashboardSidebar;
